import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useGetWorkspaceGroups } from '@/reactQuery/get';
import { WorkspaceRoles } from '@/enums';
import { WorkspaceMember, WorkspaceWithProjectCount } from '@/types';
import { useUser } from './useUser';

const useGetAllWorkspaces = () => {
  const { user } = useUser();
  const { organization_id: organizationId, workspace_id: workspaceId } = useParams();
  const { data: { items: workspaces = [] } = {}, isLoading } =
    useGetWorkspaceGroups(organizationId);

  // Memoize workspace categorization and expensive computations
  const { workspaceGroups, mergedWorkspaces, ownedWorkspacesCount } = useMemo(() => {
    // Categorize workspaces into private (single member) and shared (multiple members)
    const { priv, shared } = workspaces.reduce(
      (acc, workspace) => {
        (workspace.members.length > 1 ? acc.shared : acc.priv).push(workspace);
        return acc;
      },
      {
        priv: [] as WorkspaceWithProjectCount[],
        shared: [] as WorkspaceWithProjectCount[],
      },
    );

    const categorizedWorkspaces = { priv, shared };
    const allWorkspaces = [...priv, ...shared];

    const ownedCount = allWorkspaces.filter((ws) =>
      ws.members?.some(
        (m: WorkspaceMember) => m.user_id === user?._id && m.role === WorkspaceRoles.OWNER,
      ),
    ).length;

    return {
      workspaceGroups: categorizedWorkspaces,
      mergedWorkspaces: allWorkspaces,
      ownedWorkspacesCount: ownedCount,
    };
  }, [workspaces, user?._id]);

  const firstWorkspaceId = mergedWorkspaces?.[0]?._id;
  const currentWorkspace = mergedWorkspaces?.find(({ _id }) => _id === workspaceId);

  return {
    workspaceGroups,
    mergedWorkspaces,
    isLoading,
    firstWorkspaceId,
    currentWorkspace,
    ownedWorkspacesCount,
  };
};

export default useGetAllWorkspaces;
